import { MessageSquare, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError, useAuth } from "@/auth";
import { CommentThread } from "@/components/projects/CommentThread";
import { Avatar, MemberChecks, RichText, timeAgo } from "@/components/projects/common";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function NewMessage({ projectId, members, onDone }) {
  const { user } = useAuth();
  const others = members.filter((m) => m.id !== user.id);
  const [form, setForm] = useState({ title: "", body: "", notify: others.map((m) => m.id) });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/projects/${projectId}/messages`, form); toast.success("Discussion publiée."); onDone(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <form className="pj-box pj-form" onSubmit={submit} data-testid="message-form">
      <input placeholder="Sujet" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="message-title" />
      <textarea rows={5} placeholder="Votre message (tapez @Nom pour mentionner)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="message-body" />
      <MemberChecks members={others} value={form.notify} onChange={(notify) => setForm({ ...form, notify })} testid="message-notify" />
      <button type="submit" className="primary-button" data-testid="message-submit">Publier</button>
    </form>
  );
}

export function DiscussionsTab({ project, members, openId }) {
  const [items, setItems] = useState([]);
  const [writing, setWriting] = useState(false);
  const [current, setCurrent] = useState(null);
  const load = useCallback(() => api.get(`/projects/${project.id}/messages`).then(({ data }) => { setItems(data); return data; }).catch(() => []), [project.id]);
  useEffect(() => { load().then((d) => { const m = d.find((x) => x.id === openId); if (m) setCurrent(m); }); }, [load, openId]);
  const remove = async (m) => { if (window.confirm("Supprimer cette discussion ?")) { await api.delete(`/projects/${project.id}/messages/${m.id}`); setCurrent(null); load(); } };
  return (
    <div data-testid="discussions-tab">
      <div className="pj-toolbar"><button type="button" className="primary-button" onClick={() => setWriting(!writing)} data-testid="message-new-btn"><Plus size={15} />Nouvelle discussion</button></div>
      {writing && <NewMessage projectId={project.id} members={members} onDone={() => { setWriting(false); load(); }} />}
      {items.length === 0 && !writing && <p className="pj-empty">Aucune discussion pour l'instant.</p>}
      <ul className="pj-feed">
        {items.map((m) => (
          <li key={m.id}><button type="button" className="pj-feed-item" onClick={() => setCurrent(m)} data-testid={`message-${m.id}`}>
            <Avatar name={m.author_name} size={36} />
            <div><strong>{m.title}</strong><p>{m.body.slice(0, 160)}</p><small>{m.author_name} · {timeAgo(m.created_at)}</small></div>
            {m.comments > 0 && <span className="pj-bubble"><MessageSquare size={13} />{m.comments}</span>}
          </button></li>
        ))}
      </ul>
      {current && (
        <Dialog open onOpenChange={(o) => !o && setCurrent(null)}>
          <DialogContent aria-describedby={undefined} className="pj-dialog" data-testid="message-panel">
            <DialogTitle className="pj-dialog-title">{current.title}</DialogTitle>
            <small className="pj-sub">{current.author_name} · {timeAgo(current.created_at)}</small>
            <RichText text={current.body} members={members} />
            <button type="button" className="secondary-button pj-self-start" onClick={() => remove(current)} data-testid="message-delete"><Trash2 size={14} />Supprimer</button>
            <CommentThread projectId={project.id} targetType="message" targetId={current.id} members={members} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
